import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Mail } from 'lucide-react';
import { useSession } from '@/api/session';
import { useExportLog, useExportSettings, useUpdateExportSettings } from '@/api/hooks/exports';
import type { ExportKind, ExportLogRow } from '@/api/types';
import { formatDateTime } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Checkbox, Field, Input } from '@/components/ui/Field';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { toast } from '@/stores/toast';

export const EXPORT_KIND_LABEL: Record<ExportKind, string> = {
  transport_order: 'Заявка',
  cmr: 'ЧМР',
  invoice: 'Фактура',
};

const KINDS: ExportKind[] = ['transport_order', 'cmr', 'invoice'];

const KIND_HINT: Record<ExportKind, string> = {
  transport_order: 'например заявки@…',
  cmr: 'например cmr@…',
  invoice: 'например счетоводство@…',
};

/** „a@b.bg, c@d.bg" или по един на ред → списък. */
function parseEmails(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
}

/**
 * Документите по имейл: трите адреса, дали пощата на сървъра работи и какво
 * е тръгнало. Адресите сменя само управителят; дневникът се вижда от всички в
 * офиса — „изпратихме ли ЧМР-то на счетоводството" не бива да е въпрос.
 */
export function DocumentEmailPanel() {
  const role = useSession((st) => st.user?.role);
  const canEdit = role === 'owner';
  const settings = useExportSettings();
  const update = useUpdateExportSettings();
  const log = useExportLog();
  const [enabled, setEnabled] = useState(true);
  const [draft, setDraft] = useState<Record<ExportKind, string> | null>(null);

  useEffect(() => {
    if (!settings.data || draft) return;
    setEnabled(settings.data.enabled);
    setDraft({
      transport_order: settings.data.recipients.transport_order.join(', '),
      cmr: settings.data.recipients.cmr.join(', '),
      invoice: settings.data.recipients.invoice.join(', '),
    });
  }, [settings.data, draft]);

  if (settings.isLoading || !draft) return settings.isError ? <ErrorState onRetry={() => void settings.refetch()} /> : <LoadingBlock />;
  const data = settings.data;

  const save = () =>
    update.mutate(
      {
        enabled,
        recipients: {
          transport_order: parseEmails(draft.transport_order),
          cmr: parseEmails(draft.cmr),
          invoice: parseEmails(draft.invoice),
        },
      },
      {
        onSuccess: () => toast.ok('Адресите са записани'),
        onError: (error) => toast.error('Адресите не са записани', error instanceof Error ? error.message : undefined),
      },
    );

  return (
    <div className="space-y-3 p-3 sm:p-4">
      {data && !data.smtpConfigured ? (
        <div className="flex gap-2 rounded-lg border border-warn/40 bg-warn-soft/40 p-3 text-sm text-warn">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <div className="font-medium">Пощата на сървъра още не е настроена — писмата не тръгват.</div>
            <div className="mt-0.5 text-xs">
              Нужна е пощенска кутия, от която да се праща (SMTP). Въвежда се веднъж на сървъра; дотогава всеки
              опит остава в дневника като „неизпратено".
            </div>
          </div>
        </div>
      ) : null}

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-1.5">
              <Mail className="size-4 text-ink-dim" />
              Документите по имейл
            </span>
          }
          subtitle={
            data
              ? `Изпраща от ${data.from}. Щом заявката събере заявка, ЧМР и фактура, всеки отива на своя адрес като PDF.`
              : undefined
          }
        />
        <CardBody className="space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            {KINDS.map((kind) => (
              <Field key={kind} label={EXPORT_KIND_LABEL[kind]} hint="Няколко адреса — със запетая">
                <Input
                  value={draft[kind]}
                  disabled={!canEdit}
                  placeholder={KIND_HINT[kind]}
                  onChange={(e) => setDraft({ ...draft, [kind]: e.target.value })}
                />
              </Field>
            ))}
          </div>
          <Checkbox
            label="Изпращай автоматично, щом заявката събере трите документа"
            checked={enabled}
            disabled={!canEdit}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          {canEdit ? (
            <div className="flex justify-end">
              <Button variant="primary" loading={update.isPending} onClick={save}>
                Запиши адресите
              </Button>
            </div>
          ) : (
            <p className="text-xs text-ink-faint">Адресите се сменят от управителя.</p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Изпратени документи" subtitle="Последните 100 опита" />
        {log.isLoading ? (
          <LoadingBlock />
        ) : (log.data ?? []).length === 0 ? (
          <EmptyState title="Още нищо не е изпращано." />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Кога</TH>
                <TH>Заявка</TH>
                <TH>Документ</TH>
                <TH>До</TH>
                <TH>Състояние</TH>
                <TH>Как</TH>
              </tr>
            </THead>
            <TBody>
              {(log.data ?? []).map((row) => (
                <TR key={row.id}>
                  <TD className="whitespace-nowrap">{formatDateTime(row.createdAt)}</TD>
                  <TD>
                    {row.orderNumber ? (
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: row.orderId }}
                        className="font-mono text-accent hover:underline"
                      >
                        {row.orderNumber}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD>{EXPORT_KIND_LABEL[row.kind] ?? row.kind}</TD>
                  <TD className="max-w-[16rem] truncate" title={row.recipients.join(', ')}>
                    {row.recipients.join(', ')}
                  </TD>
                  <TD>
                    <ExportStatusBadge row={row} />
                  </TD>
                  <TD className="text-xs text-ink-dim">
                    {row.trigger === 'manual' ? `ръчно${row.sentByName ? ` · ${row.sentByName}` : ''}` : 'автоматично'}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export function ExportStatusBadge({ row }: { row: Pick<ExportLogRow, 'status' | 'error'> }) {
  if (row.status === 'sent') return <Badge tone="ok">изпратено</Badge>;
  if (row.status === 'no_smtp') return <Badge tone="warn">неизпратено — няма поща</Badge>;
  return (
    <span title={row.error ?? ''}>
      <Badge tone="danger">неуспех</Badge>
    </span>
  );
}
